import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Announcer, BrandHeader, ProgressBar, Skeleton, Spinner, StatusLine, SuccessState } from "../components/ui.jsx";
import SignaturePad from "../components/SignaturePad.jsx";
import Turnstile from "../components/Turnstile.jsx";
import { createPublicRequest, getFunction, postFunction, requestSignedUrl, track, uploadToSignedUrl } from "../lib/api.js";
import { assignFileStems, buildDecoratedName, dataUrlToBlob, formatBytes, textFile } from "../lib/files.js";
import { ADDRESS_PARTS, NAME_PARTS, answerError, answerText, emptyAnswer, formSteps, isDisplayType, isInputType, maskInput, optionsOf, prefillFromLink, prefillMode } from "../lib/formFields.js";
import { useProgress, useProgressMap } from "../lib/progress.js";
import { uploadToken } from "../lib/tokens.js";

const MAX_FILES = 10;
const MAX_SIZE = 25 * 1024 * 1024;
const ACCEPTED = ".pdf,.doc,.docx,.png,.jpg,.jpeg,.heic";

const LINK_BTN = "text-xs font-medium text-slate-500 hover:text-slate-900 bg-transparent border-0 px-1 py-2.5 -my-2.5 cursor-pointer transition-colors";
const LINK_BTN_DANGER = "text-xs font-medium text-slate-500 hover:text-red-600 bg-transparent border-0 px-1 py-2.5 -my-2.5 cursor-pointer transition-colors";
const DROP = "w-full rounded-xl border border-dashed bg-white px-4 text-center cursor-pointer transition-colors";
const DROP_IDLE = `${DROP} border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50`;
const DROP_ACTIVE = `${DROP} border-emerald-500 bg-emerald-50`;
const PRIMARY =
  "rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed";

/**
 * The upload page — the link an agent sends for documents.
 *
 * Two ways in, and they differ in whether a request exists yet:
 *   /<token>        an agent's own send, or a public form already submitted.
 *                   The request is there; this page fills it.
 *   /?form=<slug>   a public document-request link. NOTHING exists — the
 *                   template comes from the form itself, and the request is
 *                   created when the visitor presses Submit, so merely opening
 *                   a link never reaches an agent's dashboard or inbox.
 *
 * A request built from blocks ("What we need from you") shows one section per
 * requested item; one without blocks is a plain "upload any files" page.
 */
export default function UploadPage() {
  const { search, pathname } = useLocation();
  const formSlug = new URLSearchParams(search).get("form") || "";
  const token = uploadToken(search, pathname);
  const [view, setView] = useState(() => (!token && !formSlug ? { kind: "legacy", missingToken: true } : { kind: "loading" }));
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    document.title = "Upload a File ｜ 上传文件";
    if (!token && !formSlug) return undefined;
    let cancelled = false;
    // Nothing to track on a public link — the request it will become does not exist.
    if (token) track("track-upload-open", token);
    getFunction("get-document-request", formSlug ? { form: formSlug } : { token }, { fallbackError: "Template lookup failed" })
      .then((payload) => {
        if (cancelled) return;
        // Opened again after it was sent: say so, rather than offer to send again.
        if (payload?.completed) return setView({ kind: "done", already: true });
        if (payload?.config?.blocks?.length) {
          const customerName = payload.customerName || payload.config?.customerName || null;
          return setView({
            kind: "custom",
            config: { ...payload.config, customerName, blocks: assignFileStems(payload.config.blocks) },
            turnstileRequired: Boolean(payload.turnstileRequired),
            turnstileSiteKey: payload.turnstileSiteKey || "",
          });
        }
        setView({ kind: "legacy" });
      })
      .catch((error) => {
        console.warn("Template load failed", error);
        if (!cancelled) setView({ kind: "legacy" });
      });
    return () => {
      cancelled = true;
    };
  }, [token, formSlug]);

  const done = () => {
    setView({ kind: "done" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 antialiased">
      <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
        <BrandHeader />
        {view.kind === "loading" && (
          <div aria-busy="true" aria-live="polite">
            <span className="sr-only">Loading your documents ｜ 载入中</span>
            <Skeleton className="h-8 w-3/5 rounded-lg" />
            <Skeleton className="mt-4 h-4 w-4/5 rounded" />
            <Skeleton className="mt-8 h-24 w-full rounded-xl" />
            <Skeleton className="mt-6 h-24 w-full rounded-xl" />
          </div>
        )}
        {view.kind === "legacy" && <LegacyUploader token={token} missingToken={view.missingToken} onDone={done} announce={setAnnouncement} />}
        {view.kind === "custom" && (
          <RequestedItems
            config={view.config}
            initialToken={token}
            formSlug={formSlug}
            search={search}
            turnstileRequired={view.turnstileRequired}
            turnstileSiteKey={view.turnstileSiteKey}
            onDone={done}
            announce={setAnnouncement}
          />
        )}
        {view.kind === "done" &&
          (view.already ? (
            <SuccessState title="Already received ｜ 已收到资料" text="We have already received your documents. Nothing else is needed. ｜ 我们已收到您的资料，无需其他动作。" />
          ) : (
            <SuccessState title="Submission received ｜ 已收到资料" text="Thank you. Nothing else is needed. ｜ 感谢您，无需其他动作。" />
          ))}
        <Announcer message={announcement} />
      </div>
    </div>
  );
}

/**
 * Tells the agent it is done. `responses` (one per question, plain text) are
 * kept for the dashboard's Forms tab when the request came from a public form;
 * an agent's own request has nowhere to keep them, and they are ignored.
 */
async function markUploadComplete(token, responses = undefined) {
  try {
    await postFunction("mark-upload-complete", { token, ...(responses ? { responses } : {}) });
  } catch (error) {
    console.warn("Failed to mark completion", error);
  }
}

// ─── Any files ───────────────────────────────────────────────────────────────

/** "Upload your documents": up to 10 files, 25 MB each, uploaded in turn. */
function LegacyUploader({ token, missingToken, onDone, announce }) {
  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState(null); // { ok, message, retry }
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const picker = useRef(null);
  const progressFor = useProgressMap();
  const disabled = missingToken || busy;

  const accept = (files) => {
    if (!files.length) return;
    setSlots((current) => {
      const next = [...current];
      for (const file of files) {
        if (next.length >= MAX_FILES) {
          setStatus({ ok: false, message: `Maximum of ${MAX_FILES} files. ｜ 最多 ${MAX_FILES} 个档案。` });
          break;
        }
        if (file.size > MAX_SIZE) {
          setStatus({ ok: false, message: `${file.name} exceeds 25MB. ｜ 档案超过 25MB。` });
          continue;
        }
        next.push({ id: crypto.randomUUID(), file, status: "pending", message: "Ready to upload ｜ 准备上传" });
      }
      return next;
    });
  };

  const setSlot = (id, patch) => setSlots((current) => current.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  async function upload() {
    if (!token) return;
    if (!slots.length) {
      setStatus({ ok: false, message: "Please add at least one file. ｜ 请先新增档案。" });
      return;
    }
    setBusy(true);
    setStatus(null);
    announce("Uploading your files");
    let current = null;
    try {
      for (const slot of slots) {
        // A retry does not send again what already went.
        if (slot.status === "success") continue;
        current = slot;
        setSlot(slot.id, { status: "uploading", message: "Uploading… ｜ 上传中" });
        progressFor(slot.id).start(`Uploading ${slot.file.name}`);
        const signedUrl = await requestSignedUrl(token, slot.file.name);
        await uploadToSignedUrl(signedUrl, slot.file);
        progressFor(slot.id).finish("Upload complete ｜ 上传完成");
        setSlot(slot.id, { status: "success", message: "Uploaded ｜ 已完成" });
      }
      setStatus({ ok: true, message: "All files uploaded successfully! ｜ 全部档案上传完成。" });
      announce("All files uploaded");
      onDone();
      await markUploadComplete(token);
    } catch (error) {
      if (current) {
        setSlot(current.id, { status: "error", message: `${error.message || "Upload failed"} ｜ 上传失败` });
        progressFor(current.id).fail("Upload failed ｜ 上传失败");
      }
      setStatus({ ok: false, message: error.message || "Upload failed", retry: true });
      announce("Upload failed");
    } finally {
      setBusy(false);
    }
  }

  const openPicker = () => {
    if (disabled) return;
    picker.current.value = "";
    picker.current.click();
  };

  return (
    <main>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Upload your documents ｜ 上传文件</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        {missingToken
          ? "This link is missing its upload token. Please use the secure link provided by your agent.｜ 此连结缺少授权代码，请联络您的顾问。"
          : "Up to 10 files, 25MB each. PDF, DOC, DOCX, PNG, JPG or HEIC. ｜ 最多 10 个档案，单档 25MB。"}
      </p>

      <section className="mt-8" aria-label="Your files ｜ 您的档案">
        <input ref={picker} type="file" accept={ACCEPTED} multiple className="sr-only" tabIndex={-1} aria-hidden="true" onChange={(e) => accept([...(e.target.files || [])])} />
        <div
          tabIndex={0}
          role="button"
          aria-label="Add files. Choose files, or drag and drop them here. ｜ 新增档案"
          aria-disabled={disabled || undefined}
          className={`${dragging ? DROP_ACTIVE : DROP_IDLE} py-8 ${missingToken ? "pointer-events-none opacity-50" : ""}`}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (!disabled) accept([...(e.dataTransfer?.files || [])]);
          }}
        >
          <svg className="mx-auto mb-2 h-6 w-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0L8 8m4-4l4 4M4 17v1a3 3 0 003 3h10a3 3 0 003-3v-1" />
          </svg>
          <span className="block text-sm font-semibold text-emerald-700">Choose files ｜ 选择档案</span>
          <span className="mt-1 block text-xs text-slate-400">or drag and drop ｜ 或拖曳至此</span>
        </div>

        <div className="mt-2 max-h-80 overflow-y-auto">
          {!slots.length && <p className="py-5 text-center text-sm text-slate-400">No files yet. ｜ 尚未新增档案</p>}
          {slots.map((slot) => (
            <div key={slot.id} className="border-b border-slate-200 py-3 last:border-b-0">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-medium text-slate-800">{slot.file.name}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{formatBytes(slot.file.size)}</p>
                </div>
                <StatusLine state={slot.status === "pending" ? "pending" : slot.status} text={slot.message} className="mt-0.5" />
                <button
                  type="button"
                  className="-m-1 shrink-0 cursor-pointer border-0 bg-transparent p-2 text-slate-300 transition-colors hover:text-red-600"
                  aria-label={`Remove ${slot.file.name} ｜ 移除`}
                  disabled={busy}
                  onClick={() => setSlots((current) => current.filter((s) => s.id !== slot.id))}
                >
                  <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <ProgressBar progress={progressFor(slot.id)} />
            </div>
          ))}
        </div>

        {slots.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
            <span>
              {slots.length} of {MAX_FILES} files ｜ {slots.length} / {MAX_FILES} 个档案
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setSlots([]);
                setStatus(null);
              }}
              className="-my-2.5 cursor-pointer border-0 bg-transparent px-1 py-2.5 font-medium text-slate-500 transition-colors hover:text-red-600 disabled:opacity-50"
            >
              Remove all ｜ 全部清除
            </button>
          </div>
        )}
      </section>

      {status && (
        <Alert ok={status.ok} heading={status.ok ? undefined : "Upload failed ｜ 上传失败"} message={status.message} onRetry={status.retry ? upload : undefined} />
      )}

      <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="cursor-pointer rounded-full border-0 bg-transparent px-5 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-800"
        >
          Start over ｜ 重新开始
        </button>
        <div className="sm:flex-1" />
        <button type="button" onClick={upload} disabled={disabled} className={`w-full px-6 py-3 sm:w-auto ${PRIMARY}`}>
          {busy ? (
            <>
              <Spinner /> Uploading… ｜ 上传中
            </>
          ) : (
            "Upload ｜ 上传"
          )}
        </button>
      </div>
    </main>
  );
}

// ─── Requested items ─────────────────────────────────────────────────────────

// Typed fields and display blocks explain themselves; uploads say what to send.
const DESCRIPTION = {
  double: "Front and back ｜ 双面文件",
  multi: "One or more files ｜ 可上传多个文件",
  agreement: "Read and agree ｜ 条款同意",
  signature: "Sign below ｜ 请签名",
};

/** Single and front/back uploads — and any type this page does not know yet, as before. */
function isUploadType(type) {
  return !isInputType(type) && !isDisplayType(type) && type !== "agreement" && type !== "signature";
}

function slotCount(block) {
  return block.type === "double" ? block.subLabels?.length || 2 : 1;
}

/** `filled`: what the link filled in (prefillFromLink), by block id. */
function initialSelections(blocks, filled = {}) {
  const out = {};
  for (const block of blocks) {
    // A multi-file upload starts empty and grows as files are added.
    const files = isUploadType(block.type) && block.type !== "multi" ? new Array(slotCount(block)).fill(null) : [];
    out[block.id] = {
      value: filled[block.id] ? filled[block.id].value : emptyAnswer(block.type),
      checked: false,
      hasSignature: false,
      files,
      statuses: files.map(() => ({ state: "pending" })),
      sigStatus: { state: "pending", text: "Awaiting signature ｜ 等待签名" },
      shownNames: files.map(() => null),
    };
  }
  return out;
}

function isBlockComplete(block, sel) {
  if (!sel) return false;
  if (isInputType(block.type)) return !answerError({ ...block, required: true }, sel.value);
  if (block.type === "agreement") return sel.checked;
  if (block.type === "signature") return sel.hasSignature;
  return sel.files.length > 0 && sel.files.every(Boolean);
}

/** What still needs fixing, by block id — shown under each item once Submit is pressed. */
function blockErrors(blocks, sels) {
  const out = {};
  for (const block of blocks) {
    const sel = sels[block.id];
    const required = block.required !== false;
    let error = null;
    if (!sel) error = "Missing ｜ 缺少";
    else if (isInputType(block.type)) error = answerError(block, sel.value);
    else if (block.type === "agreement") error = required && !sel.checked ? "Please check the box to agree ｜ 请勾选同意" : null;
    else if (block.type === "signature") error = required && !sel.hasSignature ? "Please sign ｜ 请签名" : null;
    else if (isUploadType(block.type)) {
      const hasAny = sel.files.some(Boolean);
      if (!hasAny) error = required ? "Please choose a file ｜ 请选择文件" : null;
      else if (sel.files.some((file) => !file)) error = "Please add every side ｜ 请上传所有页面";
    }
    if (error) out[block.id] = error;
  }
  return out;
}

/** What the client answered, one entry per question in order, as the dashboard's Forms tab shows it. */
function responsesFor(blocks, sels) {
  return blocks
    .filter((block) => !isDisplayType(block.type))
    .map((block) => {
      const sel = sels[block.id];
      let value = "";
      if (isInputType(block.type)) value = answerText(block, sel?.value);
      else if (block.type === "agreement") value = sel?.checked ? "Agreed" : "Not agreed";
      else if (block.type === "signature") value = sel?.hasSignature ? "Signed" : "";
      else value = (sel?.files ?? []).filter(Boolean).map((file) => file.name).join(", ");
      return { id: block.id, label: block.label || "", type: block.type, value, step: Number(block.step) || 0 };
    });
}

/** "What we need from you": one flat section per requested item. */
function RequestedItems({ config, initialToken, formSlug, search, turnstileRequired, turnstileSiteKey, onDone, announce }) {
  const blocks = config.blocks;
  // ?carrier=… and the like: read once, when the form opens.
  const [filled] = useState(() => prefillFromLink(blocks, search));
  const modeOf = (block) => prefillMode(block, filled[block.id]);
  const [sels, setSels] = useState(() => initialSelections(blocks, filled));
  const [status, setStatus] = useState(null); // { ok, heading, message, retry }
  const [busy, setBusy] = useState(false);
  // Errors show only after the first Submit, then follow the answers live.
  const [showErrors, setShowErrors] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  // Whether the request exists yet: from the start on a /<token> link, after
  // the first Submit on a public form link.
  const [requestExists, setRequestExists] = useState(Boolean(initialToken));
  const selsRef = useRef(sels);
  const tokenRef = useRef(initialToken);
  const pads = useRef(new Map());
  const progressFor = useProgressMap();
  // What already reached the folder, so "Try again" sends only what didn't.
  const sent = useRef(new Map());
  const turnstile = useRef(null);
  const overall = useProgress();

  useEffect(() => {
    selsRef.current = sels;
  }, [sels]);

  const update = (blockId, patch) =>
    setSels((current) => ({ ...current, [blockId]: { ...current[blockId], ...(typeof patch === "function" ? patch(current[blockId]) : patch) } }));
  const setSlotStatus = (blockId, index, state, text) =>
    update(blockId, (sel) => ({ statuses: sel.statuses.map((s, i) => (i === index ? { state, text } : s)) }));

  // A hidden field is answered by the link; the client neither sees nor counts it.
  const actionable = blocks.filter((b) => !isDisplayType(b.type) && modeOf(b) !== "hide");
  const doneCount = actionable.filter((b) => isBlockComplete(b, sels[b.id])).length;
  const errors = showErrors ? blockErrors(blocks, sels) : {};

  // A form split into steps shows one at a time. Every block stays mounted
  // (hidden when off-step), so a signature drawn on step 1 survives step 2.
  const steps = formSteps(blocks.filter((b) => modeOf(b) !== "hide"));
  const [stepAt, setStepAt] = useState(0);
  const onStep = (block) => (Number(block.step) || 0) === steps[stepAt];
  const lastStep = stepAt >= steps.length - 1;
  const stepTitle = (index) => blocks.find((b) => (Number(b.step) || 0) === steps[index] && b.type === "heading")?.label || "";

  function goToStep(index) {
    setStepAt(index);
    setStatus(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function next() {
    const pending = blockErrors(blocks.filter(onStep), selsRef.current);
    if (Object.keys(pending).length) {
      setShowErrors(true);
      announce("Some required items on this step are incomplete");
      document.getElementById(`block-${Object.keys(pending)[0]}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // The next step's empty fields are not wrong yet.
    setShowErrors(false);
    goToStep(stepAt + 1);
  }

  /**
   * Creates the request, if this page arrived by a public form link — the
   * FIRST thing the submit does: nothing exists until the visitor commits,
   * but an upload needs the folder the request carries.
   */
  async function ensureRequestExists() {
    if (tokenRef.current) return tokenRef.current;
    if (!formSlug) throw new Error("This link is missing its form.");
    if (turnstileRequired && !turnstileToken) {
      throw new Error("Please complete the security check above, then submit again. ｜ 请先完成安全验证。");
    }
    try {
      const created = await createPublicRequest({ slug: formSlug, turnstileToken });
      tokenRef.current = created.token;
      setRequestExists(true);
      return created.token;
    } catch (error) {
      // A token is single-use: a retry needs a fresh one.
      turnstile.current?.reset();
      throw error;
    }
  }

  /** Uploads `file` as `name` unless this exact thing already went. */
  async function send(key, identity, name, file, type) {
    if (sent.current.get(key) === identity) return;
    const signedUrl = await requestSignedUrl(tokenRef.current, name);
    await uploadToSignedUrl(signedUrl, file, type);
    sent.current.set(key, identity);
  }

  async function submit() {
    const pending = blockErrors(blocks, selsRef.current);
    if (Object.keys(pending).length) {
      setShowErrors(true);
      // Something left on an earlier step: go back there to show it.
      const first = blocks.find((b) => pending[b.id]);
      const firstStep = steps.indexOf(Number(first.step) || 0);
      if (firstStep !== stepAt) setStepAt(firstStep);
      setStatus({ ok: false, heading: "Some items still need your attention. ｜ 尚有必填项目未完成", message: "Look for the red notes above. ｜ 请查看上方红色提示。" });
      announce("Some required items are incomplete");
      requestAnimationFrame(() => document.getElementById(`block-${first.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }
    setStatus(null);
    setBusy(true);
    overall.start("Submitting your response… ｜ 送出中");
    announce("Submitting your response");
    const pdfSections = [];
    const signatureImages = [];
    try {
      await ensureRequestExists();
      for (const block of blocks) {
        const sel = selsRef.current[block.id];
        const required = block.required !== false;
        const stem = block.fileStem;

        if (isInputType(block.type)) {
          const answer = answerText(block, sel.value);
          if (!answer && !required) continue;
          if (!answer) throw new Error(`Missing response for ${block.label}`);
          const name = `${stem}__response.txt`;
          await send(`${block.id}:text`, answer, name, textFile(`Question: ${block.label}\nAnswer: ${answer}`, name), "text/plain");
          pdfSections.push({ type: "text", label: block.label, value: answer });
          continue;
        }

        if (block.type === "agreement") {
          if (!sel.checked && required) throw new Error(`Please agree to ${block.label}`);
          const statusText = sel.checked ? "Agreed" : "Not agreed";
          const name = `${stem}__agreement.txt`;
          await send(`${block.id}:agreement`, statusText, name, textFile(`Agreement: ${block.label}\nStatus: ${statusText}`, name), "text/plain");
          pdfSections.push({ type: "agreement", label: block.label, status: statusText, content: block.content || "" });
          continue;
        }

        if (block.type === "signature") {
          if (!sel.hasSignature) {
            if (!required) continue;
            throw new Error(`Missing signature for ${block.label}`);
          }
          const sigProgress = progressFor(`${block.id}:sig`);
          update(block.id, { sigStatus: { state: "uploading", text: "Sending signature… ｜ 传送签名中" } });
          sigProgress.start("Preparing your signature… ｜ 准备签名中");
          const dataUrl = pads.current.get(block.id).toDataURL("image/png");
          const name = `${stem}__signature.png`;
          await send(`${block.id}:sig`, dataUrl, name, new File([dataUrlToBlob(dataUrl)], name, { type: "image/png" }), "image/png");
          sigProgress.finish("Signature saved ｜ 签名已储存");
          update(block.id, { sigStatus: { state: "success", text: "Signed ｜ 已签名" } });
          signatureImages.push({ label: block.label || "Signature", dataUrl, height: Number(block.signatureHeight) || 90 });
          continue;
        }

        if (block.type === "note") {
          if (block.content) pdfSections.push({ type: "note", label: block.label, content: block.content });
          continue;
        }

        if (block.type === "heading") {
          pdfSections.push({ type: "heading", label: block.label, content: block.helperText || "" });
          continue;
        }

        const files = sel.files;
        if (!files.some(Boolean)) {
          if (!required) continue;
          throw new Error(`Missing files for ${block.label}`);
        }
        if (files.some((file) => !file)) throw new Error(`Please complete all slots for ${block.label}`);

        if (block.type === "double") {
          // The photos stacked into one PDF, then each photo on its own.
          const pdfName = `${stem}.pdf`;
          const pdfIdentity = files.map((f) => `${f.name}:${f.size}:${f.lastModified}`).join("|");
          if (sent.current.get(`${block.id}:pdf`) !== pdfIdentity) {
            files.forEach((_, i) => {
              setSlotStatus(block.id, i, "processing", "Processing… ｜ 处理中");
              progressFor(`${block.id}:${i}`).start("Preparing your document… ｜ 准备文件中", 60);
            });
            const { buildPdfFromImages } = await import("../lib/uploadPdfs.js");
            const pdfBlob = await buildPdfFromImages(files);
            files.forEach((_, i) => {
              setSlotStatus(block.id, i, "uploading", "Uploading… ｜ 上传中");
              progressFor(`${block.id}:${i}`).start("Uploading your document… ｜ 上传中", 80);
            });
            await send(`${block.id}:pdf`, pdfIdentity, pdfName, new File([pdfBlob], pdfName, { type: "application/pdf" }), "application/pdf");
          }
          for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            const subLabel = block.subLabels?.[i] || `File ${i + 1}`;
            setSlotStatus(block.id, i, "uploading", "Uploading… ｜ 上传中");
            progressFor(`${block.id}:${i}`).start(`Uploading ${file.name}`, 94);
            await send(`${block.id}:${i}`, file, buildDecoratedName(stem, subLabel, file.name), file);
            progressFor(`${block.id}:${i}`).finish("Upload complete ｜ 上传完成");
            setSlotStatus(block.id, i, "success", "Uploaded ｜ 已上传");
            update(block.id, (s) => ({ shownNames: s.shownNames.map((n, j) => (j === i ? `${file.name} + ${pdfName}` : n)) }));
          }
        } else {
          for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            const subLabel = block.subLabels?.[i] || `File ${i + 1}`;
            setSlotStatus(block.id, i, "uploading", "Uploading… ｜ 上传中");
            progressFor(`${block.id}:${i}`).start(`Uploading ${file.name}`);
            await send(`${block.id}:${i}`, file, buildDecoratedName(stem, subLabel, file.name), file);
            progressFor(`${block.id}:${i}`).finish("Upload complete ｜ 上传完成");
            setSlotStatus(block.id, i, "success", "Uploaded ｜ 已上传");
          }
        }
      }

      if (pdfSections.length || signatureImages.length) {
        overall.setLabel("Finishing up… ｜ 完成中");
        const { buildSubmissionPdf } = await import("../lib/uploadPdfs.js");
        const summary = await buildSubmissionPdf(pdfSections, signatureImages, config.customerName);
        if (summary) {
          const name = "submission-summary.pdf";
          const signedUrl = await requestSignedUrl(tokenRef.current, name);
          await uploadToSignedUrl(signedUrl, new File([summary], name, { type: "application/pdf" }), "application/pdf");
        }
      }
      overall.finish("Submitted ｜ 已送出");
      announce("Submission complete");
      onDone();
      await markUploadComplete(tokenRef.current, responsesFor(blocks, selsRef.current));
    } catch (error) {
      overall.fail("Submission failed ｜ 送出失败");
      const current = selsRef.current;
      for (const block of blocks) {
        const sel = current[block.id];
        if (block.type === "signature" && sel.sigStatus.state === "uploading") {
          update(block.id, { sigStatus: { state: "error", text: "Failed ｜ 失败" } });
          progressFor(`${block.id}:sig`).fail("Could not save signature ｜ 签名未能储存");
        }
        sel.files.forEach((file, i) => {
          if (file && sel.statuses[i]?.state !== "success") {
            setSlotStatus(block.id, i, "error", "Failed ｜ 失败");
            progressFor(`${block.id}:${i}`).fail("Upload failed ｜ 上传失败");
          }
        });
      }
      setStatus({ ok: false, heading: "Something went wrong ｜ 发生问题", message: error.message || "Submission failed", retry: true });
      announce("Submission failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">What we need from you ｜ 需要您提供的资料</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        {config.customerName ? `${config.customerName} — complete the items below. ｜ 请完成以下项目` : "Complete the items below. ｜ 请完成以下项目"}
      </p>
      {actionable.length > 0 && (
        <p className="mt-3 text-xs font-medium text-slate-400">
          {doneCount} of {actionable.length} done ｜ 已完成 {doneCount} / {actionable.length}
        </p>
      )}

      {steps.length > 1 && (
        <nav className="mt-5" aria-label="Form steps">
          <p className="text-sm font-semibold text-slate-900">
            Step {stepAt + 1} of {steps.length} ｜ 第 {stepAt + 1} / {steps.length} 步
            {stepTitle(stepAt) && <span className="font-normal text-slate-500"> · {stepTitle(stepAt)}</span>}
          </p>
          <ol className="mt-2 flex gap-1.5">
            {steps.map((step, i) => (
              <li key={step} className={`h-1.5 flex-1 rounded-full ${i <= stepAt ? "bg-emerald-500" : "bg-slate-200"}`} aria-current={i === stepAt ? "step" : undefined}>
                <span className="sr-only">
                  Step {i + 1}
                  {i < stepAt ? " (done)" : ""}
                </span>
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="mt-2 grid grid-cols-2 gap-x-4">
        {blocks.map((block) => (
          <div key={block.id} className={onStep(block) && modeOf(block) !== "hide" ? "contents" : "hidden"}>
            <Block
              block={block}
              locked={modeOf(block) === "lock"}
              first={blocks.find(onStep)?.id === block.id}
              sel={sels[block.id]}
              error={errors[block.id]}
              update={(patch) => update(block.id, patch)}
              progressFor={progressFor}
              padRef={(pad) => (pad ? pads.current.set(block.id, pad) : pads.current.delete(block.id))}
              onClearPad={() => pads.current.get(block.id)?.clear()}
              busy={busy}
            />
          </div>
        ))}
      </div>

      {status && <Alert ok={status.ok} heading={status.heading} message={status.message} onRetry={status.retry ? submit : undefined} />}

      <ProgressBar progress={overall} className="mt-6" />

      {/* Only a public form link has anything to verify: there Submit creates the request. */}
      {lastStep && turnstileRequired && !requestExists && (
        <Turnstile
          ref={turnstile}
          className="mt-6"
          siteKey={turnstileSiteKey}
          action="doc-request"
          onToken={setTurnstileToken}
          onError={(message) => setStatus({ ok: false, heading: message })}
        />
      )}

      <div className="mt-6 flex gap-3">
        {stepAt > 0 && (
          <button
            type="button"
            onClick={() => goToStep(stepAt - 1)}
            disabled={busy}
            className="rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            Back ｜ 上一步
          </button>
        )}
        {lastStep ? (
          <button type="button" onClick={submit} disabled={busy} className={`flex-1 py-3.5 ${PRIMARY}`}>
            {busy ? (
              <>
                <Spinner /> Submitting… ｜ 送出中
              </>
            ) : (
              "Submit ｜ 送出"
            )}
          </button>
        ) : (
          <button type="button" onClick={next} className={`flex-1 py-3.5 ${PRIMARY}`}>
            Next ｜ 下一步
          </button>
        )}
      </div>
      <p className="mt-3 text-center text-xs text-slate-400">Your information is sent securely. ｜ 资料以加密方式传送。</p>
    </main>
  );
}

/** One requested item: heading, one line of explanation, its control, its status. */
function Block({ block, locked, first, sel, error, update, progressFor, padRef, onClearPad, busy }) {
  if (block.type === "heading") {
    return (
      <section className={`col-span-2 pb-1 ${first ? "pt-2" : "mt-4 border-t border-slate-200 pt-6"}`}>
        <h2 className="break-words text-lg font-bold tracking-tight text-slate-900">{block.label}</h2>
        {block.helperText && <p className="mt-1 text-sm leading-relaxed text-slate-500">{block.helperText}</p>}
      </section>
    );
  }
  if (isInputType(block.type)) return <Field block={block} locked={locked} value={sel.value} error={error} onChange={(value) => update({ value })} />;

  const required = block.required !== false;
  const description = block.type in DESCRIPTION ? DESCRIPTION[block.type] : block.type === "note" ? "" : "Upload one document ｜ 上传单一文件";

  return (
    <section id={`block-${block.id}`} className="col-span-2 py-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="min-w-0 break-words text-base font-semibold text-slate-900">{block.label}</h2>
        {block.type !== "note" && (
          <span className={`shrink-0 text-xs font-medium ${required ? "text-slate-500" : "text-slate-400"}`}>{required ? "Required ｜ 必填" : "Optional ｜ 选填"}</span>
        )}
      </div>
      {description && <p className="mt-1 text-sm leading-relaxed text-slate-500">{description}</p>}
      {block.helperText && block.type !== "agreement" && <p className="mt-1 text-sm leading-relaxed text-slate-500">{block.helperText}</p>}

      {block.type === "note" && (
        <div className="mt-2 space-y-1.5">
          {block.content ? (
            block.content.split(/\n+/).map((line, i) => (
              <p key={i} className="text-sm leading-relaxed text-slate-600">
                {line}
              </p>
            ))
          ) : (
            <p className="text-sm text-slate-400">No additional notes.</p>
          )}
        </div>
      )}

      {block.type === "agreement" && (
        <>
          {block.content && (
            <div className="mt-3 max-h-60 space-y-1.5 overflow-y-auto border-l-2 border-slate-200 pl-4 text-sm leading-relaxed text-slate-600">
              {block.content.split(/\n+/).map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          )}
          <label className="mt-4 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-emerald-600"
              checked={sel.checked}
              onChange={(e) => update({ checked: e.target.checked })}
            />
            <span className="text-sm leading-relaxed text-slate-700">{block.helperText || "I agree to the above terms"}</span>
          </label>
        </>
      )}

      {block.type === "signature" && (
        <div className="mt-3">
          <div className="relative overflow-hidden rounded-xl border border-slate-300 bg-white">
            {!sel.hasSignature && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-300">Sign here ｜ 请在此签名</div>
            )}
            {/* A fixed drawing size, left transparent: the PNG goes onto the summary PDF. */}
            <SignaturePad
              ref={padRef}
              resolution={{ width: 900, height: 240 }}
              background="transparent"
              strokeStyle="#111827"
              className="block w-full bg-white"
              style={{ height: 180 }}
              ariaLabel={`Signature area for ${block.label || "signature"} ｜ 签名区`}
              onChange={(signed) =>
                update({
                  hasSignature: signed,
                  sigStatus: signed ? { state: "success", text: "Signed ｜ 已签名" } : { state: "pending", text: "Awaiting signature ｜ 等待签名" },
                })
              }
            />
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <StatusLine state={sel.sigStatus.state} text={sel.sigStatus.text} />
            <button
              type="button"
              className={LINK_BTN}
              disabled={busy}
              onClick={() => {
                progressFor(`${block.id}:sig`).reset();
                // The pad's onChange puts the status back to "Awaiting signature".
                onClearPad();
              }}
            >
              Clear ｜ 清除
            </button>
          </div>
          <ProgressBar progress={progressFor(`${block.id}:sig`)} />
        </div>
      )}

      {block.type === "multi" && (
        <MultiUpload
          block={block}
          sel={sel}
          busy={busy}
          progressFor={progressFor}
          onChange={(files) => {
            files.forEach((_, i) => progressFor(`${block.id}:${i}`).reset());
            update({ files, statuses: files.map(() => ({ state: "ready", text: "Ready ｜ 已选择" })), shownNames: files.map(() => null) });
          }}
        />
      )}

      {isUploadType(block.type) && block.type !== "multi" && (
        <div className={sel.files.length > 1 ? "mt-3 grid gap-4 sm:grid-cols-2" : "mt-3"}>
          {sel.files.map((file, i) => (
            <FileSlot
              key={i}
              blockLabel={block.label}
              subLabel={sel.files.length > 1 ? block.subLabels?.[i] || `File ${i + 1}` : ""}
              accept={block.type === "double" ? "image/*" : ACCEPTED}
              file={file}
              shownName={sel.shownNames[i]}
              status={sel.statuses[i]}
              progress={progressFor(`${block.id}:${i}`)}
              busy={busy}
              onPick={(picked) => {
                progressFor(`${block.id}:${i}`).reset();
                update((s) => ({
                  files: s.files.map((f, j) => (j === i ? picked : f)),
                  statuses: s.statuses.map((st, j) => (j === i ? (picked ? { state: "ready", text: "Ready ｜ 已选择" } : { state: "pending" }) : st)),
                  shownNames: s.shownNames.map((n, j) => (j === i ? null : n)),
                }));
              }}
            />
          ))}
        </div>
      )}
      <FieldError id={`block-${block.id}-error`} message={error} />
    </section>
  );
}

const INPUT =
  "w-full rounded-xl border bg-white px-3.5 py-2.5 text-base text-slate-800 placeholder-slate-400 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed sm:text-sm";
const inputCls = (error) => `${INPUT} ${error ? "border-red-400" : "border-slate-300"}`;

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1.5 text-sm font-medium text-red-600" role="alert">
      {message}
    </p>
  );
}

// What the phone keyboard and the browser's autofill should offer per type.
const INPUT_PROPS = {
  text: { type: "text" },
  email: { type: "email", inputMode: "email", autoComplete: "email", placeholder: "name@example.com" },
  phone: { type: "tel", inputMode: "tel", autoComplete: "tel", placeholder: "(215) 555-0123" },
  number: { type: "text", inputMode: "decimal" },
  date: { type: "text", inputMode: "numeric", placeholder: "MM/DD/YYYY" },
};

// Street and unit full width, then city / state / ZIP on one row (city alone on a phone).
const ADDRESS_SPAN = {
  street: "col-span-2 sm:col-span-6",
  line2: "col-span-2 sm:col-span-6",
  city: "col-span-2 sm:col-span-3",
  state: "col-span-1",
  zip: "col-span-1 sm:col-span-2",
};

const LOCKED_NOTE = "Filled in for you ｜ 已为您填写";

/**
 * A typed field: a label, an optional hint, the control and its error.
 * `width: "half"` puts two side by side from tablet width up. `locked`: the
 * link filled it in and the agent set it not to be changed — shown, disabled.
 */
function Field({ block, locked, value, error, onChange }) {
  const id = `field-${block.id}`;
  const errorId = `block-${block.id}-error`;
  const hintId = block.helperText ? `${id}-hint` : undefined;
  const describedBy = [hintId, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  const required = block.required !== false;
  const grouped = ["radio", "checkboxes", "name", "address"].includes(block.type);
  const half = block.width === "half" && block.type !== "address";
  const Wrapper = grouped ? "fieldset" : "div";
  const Label = grouped ? "legend" : "label";
  const options = optionsOf(block);

  if (block.type === "checkbox") {
    return (
      <div id={`block-${block.id}`} className={`${half ? "col-span-2 sm:col-span-1" : "col-span-2"} min-w-0 py-3`}>
        <label
          className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-sm transition-colors ${
            value ? "border-emerald-500 bg-emerald-50" : error ? "border-red-400 bg-white" : "border-slate-300 bg-white hover:border-slate-400"
          }`}
        >
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            disabled={locked}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-emerald-600 disabled:cursor-not-allowed"
          />
          <span className="min-w-0 break-words font-medium leading-snug text-slate-800">
            {block.label}
            {required ? (
              <span className="ml-0.5 text-red-600" aria-hidden="true">
                *
              </span>
            ) : (
              <span className="ml-1.5 text-xs font-medium text-slate-400">Optional ｜ 选填</span>
            )}
          </span>
        </label>
        {block.helperText && (
          <p id={hintId} className="mt-1.5 text-sm leading-relaxed text-slate-500">
            {block.helperText}
          </p>
        )}
        {locked && <p className="mt-1.5 text-xs text-slate-400">{LOCKED_NOTE}</p>}
        <FieldError id={errorId} message={error} />
      </div>
    );
  }

  let control;
  if (block.type === "toggle") {
    control = (
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={value === true}
        aria-describedby={describedBy}
        onClick={() => onChange(!value)}
        className="inline-flex cursor-pointer items-center gap-3 rounded-full border-0 bg-transparent p-0 text-sm font-medium text-slate-700"
      >
        <span className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${value ? "bg-emerald-600" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${value ? "left-5.5" : "left-0.5"}`} />
        </span>
        {value ? "Yes ｜ 是" : "No ｜ 否"}
      </button>
    );
  } else if (block.type === "paragraph") {
    control = (
      <textarea
        id={id}
        rows={4}
        value={value}
        placeholder={block.placeholder || ""}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls(error)} resize-y`}
      />
    );
  } else if (block.type === "select") {
    control = (
      <select id={id} value={value} aria-invalid={Boolean(error)} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} className={`${inputCls(error)} cursor-pointer`}>
        <option value="">Choose… ｜ 请选择</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  } else if (block.type === "radio" || block.type === "checkboxes") {
    const multiple = block.type === "checkboxes";
    const picked = multiple ? value : [value];
    control = (
      <div className="mt-1 grid gap-2 sm:grid-cols-2" aria-describedby={describedBy}>
        {options.map((o) => {
          const on = picked.includes(o);
          return (
            <label
              key={o}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-sm transition-colors ${on ? "border-emerald-500 bg-emerald-50 text-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
            >
              <input
                type={multiple ? "checkbox" : "radio"}
                name={id}
                value={o}
                checked={on}
                onChange={(e) => onChange(multiple ? (e.target.checked ? [...value, o] : value.filter((v) => v !== o)) : o)}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-emerald-600"
              />
              <span className="min-w-0 break-words leading-snug">{o}</span>
            </label>
          );
        })}
      </div>
    );
  } else if (block.type === "name" || block.type === "address") {
    const parts = block.type === "name" ? NAME_PARTS : ADDRESS_PARTS;
    control = (
      <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-6">
        {parts.map((part) => {
          const span = block.type === "name" ? "col-span-2 sm:col-span-3" : ADDRESS_SPAN[part.key];
          return (
            <label key={part.key} className={`block min-w-0 ${span}`}>
              <span className="mb-1 block text-xs font-medium text-slate-500">
                {part.label}
                {part.required === false && <span className="text-slate-400"> · Optional ｜ 选填</span>}
              </span>
              <input
                type="text"
                autoComplete={part.autoComplete}
                inputMode={part.key === "zip" ? "numeric" : undefined}
                value={value?.[part.key] || ""}
                aria-invalid={Boolean(error)}
                onChange={(e) => onChange({ ...value, [part.key]: part.key === "state" ? e.target.value.toUpperCase().slice(0, 2) : e.target.value })}
                maxLength={part.key === "zip" ? 10 : undefined}
                className={inputCls(error && !(value?.[part.key] || "").trim() && part.required !== false ? error : null)}
              />
            </label>
          );
        })}
      </div>
    );
  } else {
    const props = INPUT_PROPS[block.type] || INPUT_PROPS.text;
    control = (
      <input
        id={id}
        {...props}
        placeholder={block.placeholder || props.placeholder || ""}
        value={value}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        onChange={(e) => onChange(maskInput(block.type, e.target.value))}
        className={inputCls(error)}
      />
    );
  }

  return (
    <Wrapper id={`block-${block.id}`} className={`${half ? "col-span-2 sm:col-span-1" : "col-span-2"} min-w-0 py-3`}>
      <Label {...(grouped ? {} : { htmlFor: id })} className="mb-1.5 block text-sm font-semibold text-slate-900">
        <span className="break-words">{block.label}</span>
        {block.type === "toggle" ? null : required ? (
          <span className="ml-0.5 text-red-600" aria-hidden="true">
            *
          </span>
        ) : (
          <span className="ml-1.5 text-xs font-medium text-slate-400">Optional ｜ 选填</span>
        )}
        {required && block.type !== "toggle" && <span className="sr-only"> (required)</span>}
      </Label>
      {block.helperText && (
        <p id={hintId} className="-mt-0.5 mb-2 text-sm leading-relaxed text-slate-500">
          {block.helperText}
        </p>
      )}
      {/* A disabled fieldset disables every control inside: inputs, choices, the toggle. */}
      {locked ? (
        <fieldset disabled className="m-0 min-w-0 border-0 p-0 opacity-80">
          {control}
        </fieldset>
      ) : (
        control
      )}
      {locked && <p className="mt-1.5 text-xs text-slate-400">{LOCKED_NOTE}</p>}
      <FieldError id={errorId} message={error} />
    </Wrapper>
  );
}

/** Several files in one item, up to `maxFiles` (10 unless the agent set it). */
function MultiUpload({ block, sel, busy, progressFor, onChange }) {
  const input = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState("");
  const max = Math.min(Math.max(Number(block.maxFiles) || 10, 1), MAX_FILES * 2);
  const files = sel.files;

  function add(picked) {
    const list = [...picked];
    const tooBig = list.filter((f) => f.size > MAX_SIZE);
    const room = max - files.length;
    const fitting = list.filter((f) => f.size <= MAX_SIZE).slice(0, Math.max(room, 0));
    const notes = [];
    if (tooBig.length) notes.push(`${tooBig.length} file(s) over 25 MB skipped ｜ 超过 25 MB 的文件已略过`);
    if (list.length - tooBig.length > fitting.length) notes.push(`Up to ${max} files ｜ 最多 ${max} 个文件`);
    setNotice(notes.join(" · "));
    if (fitting.length) onChange([...files, ...fitting]);
  }

  return (
    <div className="mt-3">
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPTED}
        className="sr-only"
        aria-label={`${block.label} — choose files`}
        onChange={(e) => {
          add(e.target.files || []);
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="mb-3 space-y-2">
          {files.map((file, i) => (
            <li key={`${file.name}-${i}`} className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
              <div className="flex items-center gap-3">
                <span className="shrink-0 font-semibold text-emerald-600" aria-hidden="true">
                  ✓
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800" title={file.name}>
                    {file.name}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">{formatBytes(file.size)}</p>
                </div>
                <StatusLine state={sel.statuses[i]?.state} text={sel.statuses[i]?.text} />
                <button type="button" className={LINK_BTN_DANGER} disabled={busy} onClick={() => onChange(files.filter((_, j) => j !== i))}>
                  Remove ｜ 移除
                </button>
              </div>
              <ProgressBar progress={progressFor(`${block.id}:${i}`)} />
            </li>
          ))}
        </ul>
      )}
      {files.length < max && (
        <button
          type="button"
          className={`${dragging ? DROP_ACTIVE : DROP_IDLE} py-5`}
          disabled={busy}
          onClick={() => input.current.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            add(e.dataTransfer?.files || []);
          }}
        >
          <span className="block text-sm font-semibold text-emerald-700">{files.length ? "Add more files ｜ 添加更多文件" : "Choose files ｜ 选择档案"}</span>
          <span className="mt-1 block text-xs text-slate-400">
            or drag and drop · up to {max} ｜ 或拖曳至此 · 最多 {max} 个
          </span>
        </button>
      )}
      {notice && <p className="mt-2 text-xs font-medium text-amber-700">{notice}</p>}
    </div>
  );
}

/** One document slot: a drop target when empty, the file with Replace / Remove when filled. */
function FileSlot({ blockLabel, subLabel, accept, file, shownName, status, progress, busy, onPick }) {
  const input = useRef(null);
  const [dragging, setDragging] = useState(false);
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="min-w-0 break-words text-sm font-medium text-slate-700">{subLabel}</span>
        <StatusLine state={status.state} text={status.text} />
      </div>
      <input
        ref={input}
        type="file"
        accept={accept}
        className="sr-only"
        aria-label={`${blockLabel} — ${subLabel || "File 1"}`}
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />
      {!file ? (
        <button
          type="button"
          className={`${dragging ? DROP_ACTIVE : DROP_IDLE} py-5`}
          disabled={busy}
          onClick={() => input.current.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const dropped = e.dataTransfer?.files?.[0];
            if (dropped) onPick(dropped);
          }}
        >
          <span className="block text-sm font-semibold text-emerald-700">Choose file ｜ 选择档案</span>
          <span className="mt-1 block text-xs text-slate-400">or drag and drop ｜ 或拖曳至此</span>
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-slate-200 bg-white px-3.5 py-3">
          <span className="shrink-0 font-semibold text-emerald-600" aria-hidden="true">
            ✓
          </span>
          <div className="min-w-[8.5rem] flex-1">
            <p className="truncate text-sm font-medium text-slate-800" title={shownName || file.name}>
              {shownName || file.name}
            </p>
            <p className="mt-0.5 text-xs text-slate-400">{formatBytes(file.size)}</p>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-4">
            <button type="button" className={LINK_BTN} disabled={busy} onClick={() => input.current.click()}>
              Replace ｜ 更换
            </button>
            <button
              type="button"
              className={LINK_BTN_DANGER}
              disabled={busy}
              onClick={() => {
                input.current.value = "";
                onPick(null);
              }}
            >
              Remove ｜ 移除
            </button>
          </div>
        </div>
      )}
      <ProgressBar progress={progress} />
    </div>
  );
}
